/**
 * Gallery scene: shared-thread Inbox (?test=maater-ui#inbox-attention).
 *
 * Art until Rowan approves it. Laid out like the live Inbox index:
 * folders in the mode panel, list on the left of the main panel, preview
 * on the right. Title row is [< > | crumb | ...]. Mock data only.
 *
 * Live gap: InboxFoldersIndex renders crumbs and records no history, so
 * the index has no back/forward. The pinned thread tab already does.
 */
import { useState } from 'react'
import {
  Archive,
  FileText,
  Inbox,
  Mail,
  MessageSquare,
  Mic,
  Moon,
  MoreVertical,
  Sparkles,
  UserPlus,
  Users,
  Workflow,
} from 'lucide-react'
import { AddressBar } from '@/components/ui/address-bar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { IconButton } from '@/components/ui/icon-button'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { ExtensionFilterBar } from '@/components/integrations/_shared/extension-filter-bar'
import { ModePanelNavItem } from '@/components/layout/sidebar/ModePanelNavItem'
import {
  INBOX_FOLDERS,
  inboxIndexCrumbs,
  type InboxFolderId,
} from '@/components/features/inbox/inbox-folder-types'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type Level = 'inbox' | 'digest' | 'log'
type Focus = 'all' | 'unread'

type Entry =
  | { kind: 'flow'; who: string; text: string }
  | { kind: 'event'; who: string; text: string; link: string }
  | { kind: 'reply'; who: string; initials: string; text: string }

type ThreadFixture = {
  id: string
  folders: InboxFolderId[]
  level: Level
  title: string
  snippet: string
  age: string
  workspace: string
  scope: string
  why: string
  unread?: boolean
  group?: string
  shadow?: string
  body: string
  recipients: string
  entries: Entry[]
}

type Frame = { folder: InboxFolderId; threadId: string }

const FOLDERS = INBOX_FOLDERS.filter((folder) => folder.id !== 'extensions')

const FOLDER_ICON = {
  inbox: Inbox,
  flows: Workflow,
  chats: MessageSquare,
  suggestions: Sparkles,
  voice: Mic,
  email: Mail,
  snoozed: Moon,
  archived: Archive,
} as const

const THREADS: ThreadFixture[] = [
  {
    id: 'ggsc',
    folders: ['inbox', 'flows'],
    level: 'inbox',
    title: 'Meeting notes ready: GGSC monthly planning',
    snippet: 'GGSC monthly planning, 48 min. You attended.',
    age: '2h',
    workspace: 'Hop Studios',
    scope: 'Shared - 3',
    why: 'Action items for you',
    unread: true,
    body: 'Action items: Rowan sends the June agenda. Chris confirms the Basecamp list.',
    recipients: 'Shared with Chris, TJ (attendees)',
    entries: [
      {
        kind: 'flow',
        who: 'Zoom notes',
        text: 'GGSC monthly planning, 48 min. You attended.',
      },
      { kind: 'event', who: 'Rowan', text: 'Linked the GGSC agenda', link: 'GGSC June agenda' },
      { kind: 'event', who: 'Rowan', text: 'Moved the notes to GGSC / meetings', link: 'GGSC / meetings' },
      {
        kind: 'reply',
        who: 'Chris',
        initials: 'CH',
        text: '@Rowan the June list is the one with the symposium row.',
      },
    ],
  },
  {
    id: 'failure',
    folders: ['inbox', 'flows'],
    level: 'inbox',
    title: 'Flow failed: ensure-workspec',
    snippet: 'File already exists, three times in an hour.',
    age: '5h',
    workspace: 'Hop Studios',
    scope: 'Only you',
    why: 'A flow you are named on failed',
    unread: true,
    body: 'Grouped into this one thread. TJ is named on the flow with you.',
    recipients: 'Only you (named on the flow, with TJ)',
    entries: [
      { kind: 'flow', who: 'ensure-workspec', text: 'File already exists, three times in an hour.' },
    ],
  },
  {
    id: 'limits',
    folders: ['inbox', 'suggestions'],
    level: 'inbox',
    title: 'Soul update: limits.md',
    snippet: 'Newest of 3 proposals. Older two stay approvable.',
    age: '1d',
    workspace: 'Hop Studios',
    scope: 'Only you',
    why: 'Decision waiting',
    unread: true,
    group: '3 proposals on limits.md',
    body: 'You drafted this. A pending proposal is a decision owed.',
    recipients: 'Only you (you drafted it)',
    entries: [
      { kind: 'flow', who: 'Soul proposal', text: 'Newest of 3 proposals on limits.md.' },
    ],
  },
  {
    id: 'one-on-one',
    folders: ['inbox', 'flows'],
    level: 'inbox',
    title: 'Meeting notes ready: Chris / Rowan',
    snippet: 'Chris and a guest, 30 min. You were not in the meeting.',
    age: '4h',
    workspace: 'Hop Studios',
    scope: 'Only you',
    why: 'You own this flow',
    shadow: 'Triage would file this to Log. You were not in the meeting.',
    body: 'Owning the flow is not a reason for Inbox when you did not attend.',
    recipients: 'Only you (flow owner)',
    entries: [
      { kind: 'flow', who: 'Zoom notes', text: 'Chris and a guest, 30 min. You were not in the meeting.' },
    ],
  },
  {
    id: 'standup',
    folders: ['inbox', 'flows'],
    level: 'digest',
    title: 'Meeting notes ready: Daily standup',
    snippet: 'Daily standup, 45 min. You attended. No action items.',
    age: '3h',
    workspace: 'Hop Studios',
    scope: 'Shared - 4',
    why: 'You attended',
    body: 'Worth knowing. Nothing is assigned to you.',
    recipients: 'Shared with Chris, TJ, Travis (attendees)',
    entries: [
      { kind: 'flow', who: 'Zoom notes', text: 'Daily standup, 45 min. You attended.' },
    ],
  },
  {
    id: 'ledger',
    folders: ['inbox', 'flows'],
    level: 'digest',
    title: 'Late-todo shadow: 0 envelopes, 11 todos',
    snippet: 'Daily preview. Nothing was sent.',
    age: '12d',
    workspace: 'Hop Studios',
    scope: 'Shared - 2',
    why: 'Review ledger',
    body: 'Would-send envelopes: 0. Late client todos stay in the preview.',
    recipients: 'Shared with TJ (named on the flow)',
    entries: [
      { kind: 'flow', who: 'Late-todo shadow', text: 'Daily preview of what the sender would have emailed.' },
    ],
  },
  {
    id: 'chat',
    folders: ['chats'],
    level: 'inbox',
    title: 'Planning next sprint with Ops',
    snippet: 'Park dual-rail inbox; ship folders first.',
    age: '1h',
    workspace: 'Hop Studios',
    scope: 'Only you',
    why: 'Your chat',
    body: 'Agent chats stay in Chats. They are not part of the triage list.',
    recipients: 'Only you',
    entries: [{ kind: 'reply', who: 'You', initials: 'RS', text: 'Park dual-rail inbox; ship folders first.' }],
  },
]

const LEVELS: { id: Level; label: string }[] = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'digest', label: 'Digest' },
  { id: 'log', label: 'Log' },
]

function levelOf(thread: ThreadFixture, overrides: Record<string, Level>): Level {
  return overrides[thread.id] ?? thread.level
}

export function InboxAttentionScene() {
  const ids = testIds.maaterUiGallery
  const [history, setHistory] = useState<Frame[]>([{ folder: 'inbox', threadId: 'ggsc' }])
  const [index, setIndex] = useState(0)
  const [level, setLevel] = useState<Level>('inbox')
  const [focus, setFocus] = useState<Focus>('all')
  const [overrides, setOverrides] = useState<Record<string, Level>>({})
  const [draft, setDraft] = useState('')
  const [replies, setReplies] = useState<Record<string, string[]>>({})
  const [note, setNote] = useState<string | null>(null)

  const frame = history[index]
  const folder = frame.folder
  const selected = frame.threadId

  function push(next: Frame) {
    if (next.folder === frame.folder && next.threadId === frame.threadId) return
    const base = history.slice(0, index + 1)
    setHistory([...base, next])
    setIndex(base.length)
    setNote(null)
  }

  const visible = THREADS.filter((thread) => {
    if (!thread.folders.includes(folder)) return false
    if (levelOf(thread, overrides) !== level) return false
    if (focus === 'unread' && !thread.unread) return false
    return true
  })
  const active = THREADS.find((thread) => thread.id === selected) ?? visible[0] ?? THREADS[0]
  const crumbs = inboxIndexCrumbs(folder, {
    onInboxClick: () => push({ folder: 'inbox', threadId: selected }),
  })

  function correct(thread: ThreadFixture) {
    const next: Level = levelOf(thread, overrides) === 'log' ? 'inbox' : 'log'
    setOverrides((prev) => ({ ...prev, [thread.id]: next }))
    setNote(next === 'log' ? 'Filed to Log. A repeat of this becomes a triage rule.' : 'Moved to Inbox.')
  }

  function send() {
    const text = draft.trim()
    if (!text) return
    setReplies((prev) => ({ ...prev, [selected]: [...(prev[selected] ?? []), text] }))
    setDraft('')
    setNote('Reply posted on the thread. Participants see it. Your own reply stays read.')
  }

  return (
    <div
      className="flex h-[min(720px,72vh)] min-h-[480px] overflow-hidden rounded-md border border-border bg-background"
      data-testid={ids.inboxAttention}
    >
      <nav
        className="ws-nav-section flex w-44 shrink-0 flex-col border-r border-border py-1"
        style={wsNavSectionStyle()}
        aria-label="Inbox folders"
      >
        {FOLDERS.map((item) => {
          const Icon = FOLDER_ICON[item.id as keyof typeof FOLDER_ICON] ?? Inbox
          const count = THREADS.filter((thread) => thread.folders.includes(item.id) && levelOf(thread, overrides) === 'inbox').length
          return (
            <ModePanelNavItem
              key={item.id}
              active={folder === item.id}
              data-testid={ids.inboxAttentionFolder(item.id)}
              onClick={() => push({ folder: item.id, threadId: selected })}
              icon={<Icon className="h-4 w-4 shrink-0" />}
              label={item.label}
              trailing={
                count > 0 && item.id !== 'archived' && item.id !== 'snoozed' ? (
                  <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground">{count}</span>
                ) : null
              }
            />
          )
        })}
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <nav
          className="flex items-center gap-2 border-b border-border bg-background/50 px-4 py-2"
          aria-label="Inbox index"
        >
          <NavigationButtons
            canGoBack={index > 0}
            canGoForward={index < history.length - 1}
            onGoBack={() => setIndex((value) => Math.max(0, value - 1))}
            onGoForward={() => setIndex((value) => Math.min(history.length - 1, value + 1))}
            backTestId={ids.inboxAttentionBack}
            forwardTestId={ids.inboxAttentionForward}
          />
          <div className="min-w-0 flex-1">
            <AddressBar segments={crumbs} containerWidth={null} />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={<MoreVertical className="h-4 w-4" />} label="Inbox options" variant="ghost" size="sm" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setNote('Search opens the inbox query.')}>
                Search
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setNote('The preview pane stays open in this scene.')}>
                Hide preview pane
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex min-h-0 flex-1">
          <div className="flex min-w-0 flex-1 flex-col border-r border-border">
            <ExtensionFilterBar
              search=""
              onSearchChange={() => undefined}
              showSearch={false}
              className="border-b border-border px-2 py-1.5"
              chips={[
                { id: 'unread', label: 'Unread' },
                { id: 'all', label: 'All' },
              ]}
              activeChipId={focus}
              onChipChange={(id) => setFocus(id as Focus)}
              trailing={
                <div className="flex items-center gap-1">
                  {LEVELS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      aria-pressed={level === item.id}
                      data-testid={ids.inboxAttentionView(item.id)}
                      onClick={() => setLevel(item.id)}
                      className={cn(
                        'rounded-md px-2 py-1 text-xs',
                        level === item.id ? 'bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              }
            />
            <ul className="min-h-0 flex-1 overflow-y-auto">
              {visible.length === 0 && (
                <li className="px-3 py-8 text-xs text-muted-foreground">Nothing in this view.</li>
              )}
              {visible.map((thread) => {
                const here = levelOf(thread, overrides)
                return (
                  <li key={thread.id} className="border-b border-border">
                    {thread.group && (
                      <p className="bg-muted/40 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                        {thread.group}
                      </p>
                    )}
                    <button
                      type="button"
                      data-testid={ids.inboxAttentionRow(thread.id)}
                      onClick={() => push({ folder, threadId: thread.id })}
                      className={cn(
                        'flex w-full items-start gap-2 px-3 py-2 text-left',
                        selected === thread.id ? 'bg-primary/10' : 'hover:bg-muted/60',
                      )}
                    >
                      <RowIcon thread={thread} />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={cn('truncate text-sm', thread.unread && 'font-semibold')}>{thread.title}</span>
                          <span className="shrink-0 text-[11px] text-muted-foreground">{thread.age}</span>
                        </span>
                        <span className="truncate text-xs text-muted-foreground">{thread.snippet}</span>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                          {thread.workspace}
                        </span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            <Users className="h-3 w-3" />
                            {thread.scope}
                          </span>
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-foreground">{thread.why}</span>
                        </span>
                      </span>
                    </button>
                    {thread.shadow && here === 'inbox' && (
                      <p className="px-3 pb-1 text-[11px] text-muted-foreground">{thread.shadow}</p>
                    )}
                    <div className="px-3 pb-2">
                      <button
                        type="button"
                        data-testid={ids.inboxAttentionCorrect(thread.id)}
                        onClick={() => correct(thread)}
                        className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
                      >
                        {here === 'log' ? 'Should be Inbox' : 'Not for me'}
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>

          <aside className="flex min-h-0 w-[min(340px,42%)] shrink-0 flex-col" data-testid={ids.inboxAttentionReader}>
            <div className="shrink-0 border-b border-border bg-background px-3 py-2">
              <h2 className="text-sm font-medium leading-snug">{active.title}</h2>
              <div className="mt-1 flex items-center gap-1">
                <p className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid={ids.inboxAttentionRecipients}>
                  {active.recipients}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 shrink-0 gap-1 px-2 text-xs"
                  data-testid={ids.inboxAttentionAddPeople}
                  onClick={() => setNote('Add people opens the org member picker.')}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Add
                </Button>
              </div>
            </div>
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-3 text-sm">
              <p>{active.body}</p>
              {active.entries.map((entry, entryIndex) => (
                <TimelineEntry key={`${entry.kind}-${entryIndex}`} entry={entry} />
              ))}
              {(replies[selected] ?? []).map((text) => (
                <TimelineEntry key={text} entry={{ kind: 'reply', who: 'Rowan', initials: 'RS', text }} />
              ))}
            </div>
            <div className="shrink-0 border-t border-border bg-background px-3 py-2">
              <label className="mb-1 block text-[11px] text-muted-foreground" htmlFor="inbox-attention-reply">
                Reply in thread. Participants see it. This does not open agent chat.
              </label>
              <textarea
                id="inbox-attention-reply"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Reply to Chris and TJ..."
                rows={2}
                data-testid={ids.inboxAttentionReply}
                className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" className="h-7 text-xs" data-testid={ids.inboxAttentionSend} onClick={send}>
                  Send
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setNote('Add to current chat keeps the open agent conversation.')}
                >
                  Add to current chat
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  data-testid={ids.inboxAttentionOpenChat}
                  onClick={() => setNote('Open in chat keeps today\'s path: the linked conversation.')}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  Open in chat
                </Button>
              </div>
              {note && <p className="mt-2 text-[11px] text-muted-foreground">{note}</p>}
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function RowIcon({ thread }: { thread: ThreadFixture }) {
  const Icon = thread.folders.includes('chats')
    ? MessageSquare
    : thread.folders.includes('suggestions')
      ? Sparkles
      : thread.folders.includes('email')
        ? Mail
        : thread.folders.includes('voice')
          ? Mic
          : Workflow
  return <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
}

function TimelineEntry({ entry }: { entry: Entry }) {
  if (entry.kind === 'reply') {
    return (
      <div className="flex gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium">
          {entry.initials}
        </span>
        <div>
          <p className="text-xs font-medium">{entry.who}</p>
          <p className="text-sm">{entry.text}</p>
        </div>
      </div>
    )
  }
  return (
    <div className="rounded-md border border-border px-2 py-1.5">
      <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
        <FileText className="h-3 w-3" />
        {entry.who}
      </p>
      <p className="text-sm">{entry.text}</p>
      {entry.kind === 'event' && (
        <p className="text-xs underline decoration-border underline-offset-2">{entry.link}</p>
      )}
    </div>
  )
}
